import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, List as ListIcon, Map as MapIcon, Crosshair, Trash2, Columns3 } from "lucide-react";
import { STATUS_OPTIONS } from "../poolConstants";

export function PoolDetailFilters({ filters, setFilters, applyFilter, onSearchEnter, locations, radii = [], view, setView, viewingDeleted, deletedCount = 0, onToggleDeleted, onEmptyTrash, deletedUsers = [], deletedUserFilter = "all", onDeletedUserFilter }) {
  return (
    <Card>
      <CardContent className="py-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Input placeholder="Suchen (Name, Ort, PLZ)…" value={filters.q}
                 onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 onKeyDown={(e) => e.key === "Enter" && onSearchEnter(filters)}
                 className="w-52" data-testid="pool-search-input" />
        </div>
        <Select value={filters.status} onValueChange={(v) => applyFilter({ status: v })}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.location} onValueChange={(v) => applyFilter({ location: v })}>
          <SelectTrigger className="w-52" data-testid="filter-location"><SelectValue placeholder="Herkunft" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Orte</SelectItem>
            {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.radius} onValueChange={(v) => applyFilter({ radius: v })}>
          <SelectTrigger className="w-44" data-testid="filter-radius"><SelectValue placeholder="Umkreis" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Umkreise</SelectItem>
            {radii.map((r) => <SelectItem key={r} value={String(r)}>Umkreis {r} km</SelectItem>)}
          </SelectContent>
        </Select>
        {radii.length > 1 && (() => {
          const smallest = String(radii[0]);
          const active = filters.radius === smallest;
          return (
            <Button
              type="button"
              variant={active ? "default" : "outline"}
              size="sm"
              onClick={() => applyFilter({ radius: active ? "all" : smallest })}
              data-testid="filter-smallest-radius-chip"
              title={`Nur Praxen aus dem engsten Suchradius (${radii[0]} km)`}
            >
              <Crosshair className="h-4 w-4 mr-1.5" /> Nur engster Radius ({radii[0]} km)
            </Button>
          );
        })()}
        <Select value={filters.is_customer} onValueChange={(v) => applyFilter({ is_customer: v })}>
          <SelectTrigger className="w-44" data-testid="filter-customer"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="no">Keine Bestandskunden</SelectItem>
            <SelectItem value="yes">Nur Bestandskunden</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.has_hours} onValueChange={(v) => applyFilter({ has_hours: v })}>
          <SelectTrigger className="w-48" data-testid="filter-hours"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Öffnungszeiten: alle</SelectItem>
            <SelectItem value="yes">Mit Öffnungszeiten</SelectItem>
            <SelectItem value="no">Ohne Öffnungszeiten</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={filters.only_new} onCheckedChange={(c) => applyFilter({ only_new: !!c })} data-testid="filter-only-new" />
          Nur neue
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={filters.has_email} onCheckedChange={(c) => applyFilter({ has_email: !!c })} data-testid="filter-has-email" />
          Nur mit E-Mail
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={filters.has_doctolib} onCheckedChange={(c) => applyFilter({ has_doctolib: !!c })} data-testid="filter-has-doctolib" />
          Nur mit Doctolib-Terminkalender
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={filters.hide_excluded} onCheckedChange={(c) => applyFilter({ hide_excluded: !!c })} data-testid="filter-hide-excluded" />
          Ausgeschlossene ausblenden
        </label>
        <Button
          type="button"
          variant={viewingDeleted ? "default" : "outline"}
          size="sm"
          onClick={onToggleDeleted}
          data-testid="filter-toggle-deleted"
          title="Papierkorb – gelöschte Praxen anzeigen und wiederherstellen"
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Gelöschte{deletedCount > 0 ? ` (${deletedCount})` : ""}
        </Button>
        {viewingDeleted && deletedUsers.length > 0 && (
          <Select value={deletedUserFilter} onValueChange={(v) => onDeletedUserFilter && onDeletedUserFilter(v)}>
            <SelectTrigger className="w-52" data-testid="filter-deleted-user"><SelectValue placeholder="Gelöscht von" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Gelöscht von: alle</SelectItem>
              {deletedUsers.map((u) => <SelectItem key={u} value={u}>{u.split("@")[0]}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {viewingDeleted && deletedCount > 0 && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onEmptyTrash}
            data-testid="filter-empty-trash"
            title="Alle gelöschten Praxen endgültig entfernen"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Papierkorb leeren
          </Button>
        )}
        {viewingDeleted && (
          <span className="text-xs text-muted-foreground" data-testid="trash-retention-hint">
            Automatische endgültige Löschung nach 30 Tagen
          </span>
        )}
        <div className="ml-auto inline-flex rounded-md border overflow-hidden" data-testid="view-toggle">
          <Button variant={view === "table" ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("table")} data-testid="view-table-btn">
            <ListIcon className="h-4 w-4 mr-1" /> Tabelle
          </Button>
          <Button variant={view === "map" ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("map")} data-testid="view-map-btn">
            <MapIcon className="h-4 w-4 mr-1" /> Karte
          </Button>
          <Button variant={view === "kanban" ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("kanban")} data-testid="view-kanban-btn">
            <Columns3 className="h-4 w-4 mr-1" /> Kanban
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
